/* eslint-disable max-len */
import { Component } from '@angular/core';
import { AbstractInputComponent } from '@shared/form-fields/abstract-value-accessor/abstract-value';
import { makeProvider } from '@shared/form-fields/make-provider';
import type { AppSelectOption } from '@shared/form-fields/options/select-option';
import { round } from 'lodash-es';
import type {
    DurationRoundingStyle,
    IDurationRounding } from '../contracts/common/duration-rounding/duration-rounding';
import {
    DurationRounding,
    RestrictDuration
} from '../contracts/common/duration-rounding/duration-rounding';

@Component({
    selector: 'app-duration-rounding',
    template: `
        <div class="form-row">
            <app-duration-rounding-style-select class="flex"
                                                label="Rounding style"
                                                [(ngModel)]="roundingStyle"
                                                [disabled]="disabled" />

            <app-select-form-field label="Restrict duration"
                                   class="flex-50"
                                   *ngIf="value?.style"
                                   [disabled]="disabled"
                                   [(ngModel)]="value.restrict_duration"
                                   [options]="restrictDurationSelectOptions" />
        </div>

        <ng-container *ngIf="value">
            <div class="form-row" *ngIf="shouldEnableRoundTo()">
                <app-select-form-field class="flex-50"
                                       label="Minutes"
                                       [(ngModel)]="value.to"
                                       [options]="roundToSelectOptions"
                                       [disabled]="disabled" />

                <app-input-number label="Middle Point (minutes)"
                                  class="flex-50"
                                  [(ngModel)]="midpointMinutes"
                                  [clearable]="true"
                                  [min]="0"
                                  [max]="value.to"
                                  [step]=".05"
                                  hint="Optional. When it should round up, within the {{ value.to }} minutes selected. NOTE: This field is using minute decimals. 1m 30s = 1.5" />
            </div>

            <div *ngIf="!disabled && ((value.style && value.to) || value.restrict_duration)">
                <hr class="app-separator">

                <h6 class="form-section-header">Preview Rounding</h6>

                <p class="hint">
                    Enter a duration to see the resulting value after the selected rounding style is
                    applied.
                </p>

                <div class="form-row">
                    <app-input-duration class="flex-50"
                                        label="Example Duration"
                                        [(ngModel)]="example.value"
                                        (ngModelChange)="_calculateExampleRoundingValue()" />

                    <app-input-duration class="flex-50"
                                        label="Planned Duration"
                                        *ngIf="value.restrict_duration"
                                        [(ngModel)]="example.scheduled_duration"
                                        (ngModelChange)="_calculateExampleRoundingValue()" />

                    <app-input-duration class="flex-50"
                                        label="Rounded Example"
                                        [(ngModel)]="example.roundedValue"
                                        [disabled]="true" />
                </div>
            </div>

            <hr class="app-separator">

            <app-input-duration label="Minimum Duration"
                                style="width: 100%"
                                [(ngModel)]="value.minimum_seconds"
                                hint="Absolute minimum duration that will be used for calculations, irrespective of the 'minimum scheduled duration' option"
                                [disabled]="disabled" />
        </ng-container>
    `,
    styles: [`
        :host {
            display: block;
        }`,
    ],
    providers: [makeProvider(DurationRoundingComponent)],
})
export class DurationRoundingComponent extends AbstractInputComponent<IDurationRounding> {
    roundToSelectOptions: AppSelectOption<number>[] = [
        { value: 1, label: '1 minute' },
        { value: 5, label: '5 minutes' },
        { value: 10, label: '10 minutes' },
        { value: 15, label: '15 minutes' },
        { value: 20, label: '20 minutes' },
        { value: 25, label: '25 minutes' },
        { value: 30, label: '30 minutes' },
    ];

    restrictDurationSelectOptions = [
        { value: null, label: 'No restriction' },
        { value: RestrictDuration.Minimum, label: 'Always use planned duration as minimum' },
        { value: RestrictDuration.Maximum, label: 'Always use planned duration as maximum' },
    ];

    example = {
        value: 20 * 60,
        scheduled_duration: 25 * 60,
        roundedValue: null,
    };

    private _roundingStyle: DurationRoundingStyle;

    get roundingStyle(): DurationRoundingStyle {
        return this.value?.style;
    }

    set roundingStyle(value: DurationRoundingStyle) {
        if (value === undefined) {
            return;
        }

        this._roundingStyle = value;

        if (this._value) {
            this.value.style = value;
        }

        if (this._value === null) {
            this.value = {
                style: value,
                to: undefined,
                minimum_seconds: null,
                restrict_duration: undefined,
                midpoint_seconds: null,
            };
        }
    }

    get midpointMinutes(): number {
        const seconds = this.value?.midpoint_seconds;

        if (isNaN(seconds) || seconds === null || seconds === undefined) {
            return undefined;
        }

        return round(seconds / 60, 2);
    }

    set midpointMinutes(value: number) {
        if (isNaN(value) || value === null || value === undefined) {
            this.value.midpoint_seconds = undefined;
        } else {
            this.value.midpoint_seconds = value * 60;
        }
    }

    onValueChanged(value) {
        super.onValueChanged(value);

        this._calculateExampleRoundingValue();
    }

    _calculateExampleRoundingValue(): void {
        const rounding = DurationRounding.fromObject(this.value);

        if (this.example.value && rounding) {
            this.example.roundedValue = rounding.roundSeconds(
                this.example.value,
                this.example.scheduled_duration,
            );
        } else {
            this.example.roundedValue = null;
        }
    }

    shouldEnableRoundTo(): boolean {
        if (!this.value) {
            return false;
        }

        return ['round', 'ceil', 'floor'].indexOf(this.value.style) > -1;
    }
}
